import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import PageHeader from '@features/PageHeader';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import { useTranslation } from '@hooks';
import { alertActions, couponsActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { couponsService } from '@services';
import { ModalType } from '@constants/modal';
import { CouponStatus } from '@constants/coupons';
import { ROLE } from '@constants/user';
import { CouponIcon } from 'assets/images/icons';
import EditModal from './EditModal';
import CouponsTable from './CouponsTable';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: 16,
        height: 'calc(var(--100vh) - 108px)',
        overflowY: 'auto',
    },
});

const CouponsPage = () => {
    const { t } = useTranslation();

    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [modal, setModal] = useState({ open: false, type: ModalType.Create });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

    const selectedCouponRef = useRef({});

    const companies = useSelector(state => state.account?.account?.companies || []);
    const user = useSelector(state => state.auth.user);
    const brands = useSelector(state => state.brands.data);
    const { data } = useSelector(state => state.coupons);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(couponsActions.get(page, rowsPerPage));
    }, [page, rowsPerPage, dispatch]);

    const onClickCoupon = (coupon, type) => {
        setSelectedCoupon(coupon);
        selectedCouponRef.current = coupon;
        switch (type) {
            case 'archive':
                setDeleteModalOpen(true);
                break;
            case 'edit':
                setModal({ open: true, type: ModalType.Edit });
                break;
            case 'view':
                setModal({ open: true, type: ModalType.View });
                break;
            case 'pause':
                onModifyCoupon('pause');
                break;
            case 'resume':
                onModifyCoupon('resume');
                break;
            default:
                break;
        }
    };

    const AlertMessagesMap = {
        archive: 'delete',
        pause: 'edit',
        resume: 'edit',
    };

    const StatusMachineMap = {
        pause: CouponStatus.Paused,
        resume: CouponStatus.Active,
    };

    const onModifyCoupon = async operation => {
        try {
            await couponsService.updateCouponStatus(selectedCouponRef.current?.id, operation);
            if (operation === 'archive') {
                setDeleteModalOpen(false);
                dispatch(couponsActions.get(page, rowsPerPage));
                dispatch(alertActions.success(t(`alertMessages.${AlertMessagesMap[operation]}Success`)));
                return;
            }
            const couponsCopy = data.slice();
            const updatedCouponIndex = couponsCopy.findIndex(c => c.id === selectedCouponRef.current?.id);
            if (updatedCouponIndex !== -1) {
                couponsCopy[updatedCouponIndex].status = StatusMachineMap[operation];
                dispatch(couponsActions.setCoupons(couponsCopy));
            }
            dispatch(alertActions.success(t(`alertMessages.${AlertMessagesMap[operation]}Success`)));
        } catch (error) {
            handleApiError(error, `alertMessages.${AlertMessagesMap[operation]}Fail`, { amount: error.count });
        }
        setSelectedCoupon(null);
        selectedCouponRef.current = {};
    };

    const onAddCouponClick = () => setModal({ open: true, type: ModalType.Create });

    return (
        <Container>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<CouponIcon />}
                title={t('CouponsPage.title')}
                subtitle={t('CouponsPage.subtitle')}
            >
                {user.role !== ROLE.USER && (
                    <Button variant="contained" onClick={onAddCouponClick}>
                        {t('CouponsPage.createNewCoupon')}
                    </Button>
                )}
            </PageHeader>
            <CouponsTable
                companies={companies}
                brands={brands}
                setPage={setPage}
                page={page}
                setRowsPerPage={setRowsPerPage}
                rowsPerPage={rowsPerPage}
                onClickCoupon={onClickCoupon}
            />
            {modal.open ? (
                <EditModal
                    modal={modal}
                    setModal={setModal}
                    brands={brands}
                    selectedCoupon={selectedCoupon}
                    setSelectedCoupon={setSelectedCoupon}
                    rowsPerPage={rowsPerPage}
                    page={page}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={() => onModifyCoupon('archive')}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            )}
        </Container>
    );
};

export default CouponsPage;
